import * as posActions from "../../lib/posActions";
import * as posModels from "../../lib/api/posModels";
import * as Sentry from "@sentry/capacitor";
import * as till from "../../lib/api/till";
import AddCashAmount from "../AddCashAmount";
import cn from "classnames";
import describeNetworkError from "../../lib/describeNetworkError";
import ErrorModal from "../ErrorModal";
import logger from "../../lib/sentryLogger";
import ManagerHeader from "../ManagerHeader";
import OutlineButton from "../OutlineButton";
import React, {useEffect, useRef, useState} from "react";
import StyledKeyboard from "../StyledKeyboard";
import styles from "./ManagerPaidInsOuts.module.css";
import TextInput from "../TextInput";
import TextModal from "../TextModal";
import ToggleButton from "../ToggleButton";
import useOnChangeCashAmount from "../../lib/useOnChangeCashAmount";
import {API, Lib} from "habit-core";
import {logManagerAction} from "../../lib/api/logs";
import {openCashDrawer} from "../../lib/CashDrawer/CashDrawerPlugin";
import {useAppDispatch, useAppSelector} from "../../lib/hooks";

const strings = {
    title: "Enter New Paid In or Out",
    paidIn: "Paid In",
    paidOut: "Paid Out",
    selectReason: "Select Reason:",
    changeReplenishment: "Change Replenishment",
    purchaseRefund: "Purchase / Vendor Refund",
    pettyCashReturn: "Petty Cash Return",
    vendorPayment: "Vendor Payment",
    employeeReimbursement: "Employee Reimbursement",
    officeSupplies: "Office Supplies",
    foodPurchase: "Food Purchase",
    beveragePurchase: "Beverage Purchase",
    paperPurchase: "Paper Purchase",
    repair: "Repair & Maintenance",
    enterDollarAmount: "Enter Dollar Amount:",
    notesLabel: "Notes:",
    notesPlaceholder: "Enter Notes Here (optional)",
    submit: "Submit",
    requestSent: "Request Sent",
    successMessage: (mode: "in" | "out", amountCents: number) =>
        `Successfully paid ${mode} ${Lib.currency.centsToDollarString(
            amountCents,
        )}`,
    ok: "Ok",
    cashDrawerError:
        "An unexpected error occurred when establishing a connection with the cash drawer.",
    cashDrawerErrorWithMessage: (errMessage: string) =>
        `There was an error related to the cash drawer: ${errMessage}`,
    reportPaidInOutError: (mode: "in" | "out") =>
        `An error occurred when recording this paid ${mode}. Please close the till without ${
            mode === "in" ? "adding" : "removing"
        } cash and try again.`,
};

const PAID_IN_REASON_LABELS: {[Reason in posModels.PaidInReason]: string} = {
    change_replenishment: strings.changeReplenishment,
    purchase_refund: strings.purchaseRefund,
    petty_cash_return: strings.pettyCashReturn,
} as const;
const PAID_OUT_REASON_LABELS: {[Reason in posModels.PaidOutReason]: string} = {
    vendor_payment: strings.vendorPayment,
    employee_reimbursement: strings.employeeReimbursement,
    office_supplies: strings.officeSupplies,
    food_purchase: strings.foodPurchase,
    beverage_purchase: strings.beveragePurchase,
    paper_purchase: strings.paperPurchase,
    repair: strings.repair,
} as const;

const ManagerPaidInsOuts = () => {
    const dispatch = useAppDispatch();
    const cashier = useAppSelector((state) => state.pos.station.cashier);
    const assignedTill = useAppSelector(
        (state) => state.pos.station.assignedTill,
    );
    const stationMode = useAppSelector((state) => state.pos.station.mode);
    const isTraining = useAppSelector((state) => state.pos.station.isTraining);

    const [cashAmountCents, setCashAmountCents] =
        useState<API.models.USDCents | null>(null);
    const [displayedCashAmount, setDisplayedCashAmount] = useState("");
    const [mode, setMode] = useState<"in" | "out" | null>(null);
    const [reason, setReason] = useState<
        posModels.PaidInReason | posModels.PaidOutReason | null
    >(null);
    const [notes, setNotes] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    /* Keyboard handling */
    const [keyboardCollapsed, setKeyboardCollapsed] = useState(true);
    const [focusedInput, setFocusedInput] = useState<"cash" | "notes">("cash");
    const cashAmountRef = useRef<HTMLInputElement>(null);
    const notesRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!keyboardCollapsed) {
            if (
                cashAmountRef.current &&
                document.activeElement === cashAmountRef.current
            ) {
                cashAmountRef.current.scrollIntoView();
            } else if (
                notesRef.current &&
                document.activeElement === notesRef.current
            ) {
                notesRef.current.scrollIntoView();
            }
        }
    }, [keyboardCollapsed]);

    useEffect(() => {
        const floatVal = parseFloat(displayedCashAmount);
        if (
            isNaN(floatVal) ||
            cashAmountCents !== Lib.currency.dollarsFloatToCents(floatVal)
        ) {
            setDisplayedCashAmount(
                cashAmountCents
                    ? Lib.currency.centsToDollarString(cashAmountCents, true)
                    : "",
            );
        }
    }, [cashAmountCents]);

    const onChangeCashAmount = useOnChangeCashAmount(
        setDisplayedCashAmount,
        setCashAmountCents,
    );

    const submitDisabled = isLoading || !mode || !reason || !cashAmountCents;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleReportPaidInOutError = (err: any) => {
        logger.error(
            `Manager Paid In/Out - Failed to complete paid ${mode} call.`,
            {
                errorMessage: describeNetworkError(err).join("\n"),
            },
        );
        setErrorMessage(strings.reportPaidInOutError(mode ?? "in")); // mode should never be null in this case
    };

    const openDrawer = async () => {
        if (submitDisabled) {
            return;
        }

        if (cashier.type !== "manager") {
            logger.warn(
                `Manager Paid In/Out - Attempting to access paid ${mode}s when not in manager mode.`,
            );
            return;
        }

        setIsLoading(true);

        try {
            await openCashDrawer(isTraining);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            const errMsg = err?.message
                ? strings.cashDrawerErrorWithMessage(err.message)
                : strings.cashDrawerError;
            Sentry.captureMessage(errMsg);
            setErrorMessage(errMsg);
            setIsLoading(false);
            return;
        }

        if (mode === "in") {
            till.paidIn(
                cashier.managerId,
                assignedTill,
                reason as posModels.PaidInReason, // Already checking mode above
                cashAmountCents,
                notes ? notes : undefined,
            )
                .then(() => {
                    logManagerAction(
                        "cash-paid-in",
                        cashier.managerId,
                        stationMode,
                    );
                    setShowSuccessModal(true);
                })
                .catch(handleReportPaidInOutError)
                .finally(() => setIsLoading(false));
            return;
        }

        till.paidOut(
            cashier.managerId,
            assignedTill,
            reason as posModels.PaidOutReason, // Already checking mode above
            cashAmountCents,
            notes ? notes : undefined,
        )
            .then(() => {
                logManagerAction(
                    "cash-paid-out",
                    cashier.managerId,
                    stationMode,
                );
                setShowSuccessModal(true);
            })
            .catch(handleReportPaidInOutError)
            .finally(() => setIsLoading(false));
    };

    const renderReasons = () => (
        <>
            <div className={styles.text}>{strings.selectReason}</div>
            <div className={styles.reasons}>
                {
                    // Using explicit ternary rather than one expression to satisfy typing rules
                    mode === "in"
                        ? posModels.PAID_IN_REASONS.map((r) => (
                              <ToggleButton
                                  key={r}
                                  className={styles.toggleButton}
                                  label={PAID_IN_REASON_LABELS[r]}
                                  checked={r === reason}
                                  onChange={() => setReason(r)}
                                  disabled={isLoading}
                              />
                          ))
                        : posModels.PAID_OUT_REASONS.map((r) => (
                              <ToggleButton
                                  key={r}
                                  className={styles.toggleButton}
                                  label={PAID_OUT_REASON_LABELS[r]}
                                  checked={r === reason}
                                  onChange={() => setReason(r)}
                                  disabled={isLoading}
                              />
                          ))
                }
            </div>
        </>
    );

    return (
        <>
            <div className={styles.container}>
                <ManagerHeader label={strings.title} />
                <div className={styles.content}>
                    <div className={styles.paidInOutToggle}>
                        <ToggleButton
                            className={styles.toggleButton}
                            label={strings.paidIn}
                            checked={mode === "in"}
                            onChange={() => {
                                setMode("in");
                                setReason(null);
                            }}
                            disabled={isLoading}
                        />
                        <ToggleButton
                            className={styles.toggleButton}
                            label={strings.paidOut}
                            checked={mode === "out"}
                            onChange={() => {
                                setMode("out");
                                setReason(null);
                            }}
                            disabled={isLoading}
                        />
                    </div>
                    {mode !== null ? (
                        <>
                            {renderReasons()}
                            <AddCashAmount
                                ref={cashAmountRef}
                                className={styles.addCash}
                                label={strings.enterDollarAmount}
                                cashAmountCents={cashAmountCents}
                                setCashAmountCents={setCashAmountCents}
                                displayedCashAmount={displayedCashAmount}
                                onFocus={() => {
                                    setFocusedInput("cash");
                                    setKeyboardCollapsed(false);
                                }}
                                disabled={isLoading}
                            />
                            <div className={styles.text}>
                                {strings.notesLabel}
                            </div>
                            <TextInput
                                ref={notesRef}
                                className={styles.notes}
                                placeholder={strings.notesPlaceholder}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                onFocus={() => {
                                    setFocusedInput("notes");
                                    setKeyboardCollapsed(false);
                                }}
                                inputMode="none"
                                disabled={isLoading}
                            />
                        </>
                    ) : null}
                </div>
                <div className={styles.footer}>
                    <OutlineButton
                        className={styles.footerButton}
                        label={strings.submit}
                        disabled={submitDisabled}
                        onClick={openDrawer}
                        loading={isLoading}
                    />
                </div>
                <div
                    className={cn(
                        styles.keyboardContainer,
                        keyboardCollapsed && styles.keyboardContainerCollapsed,
                    )}>
                    <StyledKeyboard
                        currentInput={
                            focusedInput === "cash"
                                ? displayedCashAmount
                                : notes
                        }
                        visible={!keyboardCollapsed}
                        setVisible={(val) => setKeyboardCollapsed(!val)}
                        onChange={
                            focusedInput === "cash"
                                ? onChangeCashAmount
                                : (val) => setNotes(val)
                        }
                        inputRefs={[cashAmountRef, notesRef]}
                        layout={focusedInput === "cash" ? "numeric" : "default"}
                    />
                </div>
            </div>
            {showSuccessModal ? (
                <TextModal
                    title={strings.requestSent}
                    content={strings.successMessage(
                        // mode and cashAmountCents should always have a value in this case
                        mode ?? "in",
                        cashAmountCents ?? 0,
                    )}
                    cancelLabel={strings.ok}
                    onClose={() =>
                        dispatch(posActions.setStationCashier({type: "user"}))
                    }
                />
            ) : null}
            {errorMessage ? (
                <ErrorModal
                    errorMessage={errorMessage}
                    onClose={() => setErrorMessage("")}
                    showITInfo
                />
            ) : null}
        </>
    );
};

export default ManagerPaidInsOuts;
